/**
 * Cálculo puro do método LMS da OMS (sem React, sem dados de paciente).
 *
 * z = ((y / M)^L - 1) / (L * S)       (L = 0: z = ln(y / M) / S)
 * y = M * (1 + L * S * z)^(1 / L)     (L = 0: y = M * e^(S * z))
 *
 * Para indicadores de peso e IMC a OMS restringe a cauda acima de |z| = 3
 * ("restricted application of the LMS method"): fora de ±3 o escore cresce
 * linearmente pela distância entre os escores 2 e 3.
 */

export const valueAtZ = ({ L, M, S }, z) => {
    if (L === 0) return M * Math.exp(S * z);
    const base = 1 + L * S * z;
    if (base <= 0) return null;
    return M * Math.pow(base, 1 / L);
};

const rawZ = (y, { L, M, S }) => (L === 0 ? Math.log(y / M) / S : (Math.pow(y / M, L) - 1) / (L * S));

export const zScore = (y, lms, restricted = false) => {
    if (!(y > 0) || !lms) return null;
    const z = rawZ(y, lms);
    if (!restricted || Math.abs(z) <= 3) return z;
    if (z > 3) {
        const sd3 = valueAtZ(lms, 3);
        const sd2 = valueAtZ(lms, 2);
        return 3 + (y - sd3) / (sd3 - sd2);
    }
    const sd3 = valueAtZ(lms, -3);
    const sd2 = valueAtZ(lms, -2);
    return -3 + (y - sd3) / (sd2 - sd3);
};

/** Função de distribuição normal padrão (Abramowitz-Stegun 7.1.26 sobre erf, erro < 1,5e-7). */
export const normalCdf = (z) => {
    const x = Math.abs(z) / Math.SQRT2;
    const t = 1 / (1 + 0.3275911 * x);
    const poly = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
    const erf = 1 - poly * Math.exp(-x * x);
    return z >= 0 ? (1 + erf) / 2 : (1 - erf) / 2;
};

export const percentileFromZ = (z) => (z == null ? null : normalCdf(z) * 100);

/** Escores-z das linhas desenhadas; `kind` define o traço (mediana, interna, externa). */
export const PERCENTILE_LINES = [
    { label: 'P3', z: -1.880794, kind: 'outer' },
    { label: 'P15', z: -1.036433, kind: 'inner' },
    { label: 'P50', z: 0, kind: 'median' },
    { label: 'P85', z: 1.036433, kind: 'inner' },
    { label: 'P97', z: 1.880794, kind: 'outer' },
];

export const Z_LINES = [
    { label: '-3', z: -3, kind: 'outer' },
    { label: '-2', z: -2, kind: 'inner' },
    { label: '0', z: 0, kind: 'median' },
    { label: '+2', z: 2, kind: 'inner' },
    { label: '+3', z: 3, kind: 'outer' },
];

/**
 * L, M e S na idade `x` por interpolação linear entre as linhas vizinhas
 * de uma tabela `[[idade, L, M, S], ...]` ordenada. Fora da tabela: null.
 */
export const lmsAt = (rows, x) => {
    if (!rows || !rows.length || x < rows[0][0] || x > rows[rows.length - 1][0]) return null;
    const hi = rows.findIndex((row) => row[0] >= x);
    if (rows[hi][0] === x) {
        const [, L, M, S] = rows[hi];
        return { L, M, S };
    }
    const a = rows[hi - 1];
    const b = rows[hi];
    const f = (x - a[0]) / (b[0] - a[0]);
    const mix = (i) => a[i] + (b[i] - a[i]) * f;
    return { L: mix(1), M: mix(2), S: mix(3) };
};
